<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';

const props = defineProps({
  importJob: {
    type: Object,
    required: true,
  },
  inboxId: {
    type: [String, Number],
    required: true,
  },
});

const store = useStore();
const { t } = useI18n();
const showAlert = useAlert();

const liveStats = ref({ ...props.importJob });
const liveLog = ref([]);
let subscription = null;

const isRunning = computed(() => liveStats.value.status === 'running');
const isPaused = computed(() => liveStats.value.status === 'paused');
const isActive = computed(
  () =>
    isRunning.value || isPaused.value || liveStats.value.status === 'pending'
);

const progressPercent = computed(() => {
  const total = liveStats.value.total_conversations_found;
  const done =
    liveStats.value.conversations_imported +
    liveStats.value.conversations_skipped;
  if (!total || total === 0) return 0;
  return Math.min(100, Math.round((done / total) * 100));
});

function connectCable() {
  if (!window.actionCable) return;
  subscription = window.actionCable.subscriptions.create(
    {
      channel: 'FacebookImportChannel',
      import_job_id: props.importJob.id,
    },
    {
      received(data) {
        if (data.type === 'progress') {
          liveStats.value = { ...liveStats.value, ...data };
        } else if (data.type === 'conversation_complete') {
          liveLog.value.unshift(data);
          if (liveLog.value.length > 10) liveLog.value.pop();
        } else if (data.type === 'completed') {
          liveStats.value.status = 'completed';
          store.dispatch('facebookImports/fetch', props.inboxId);
        } else if (data.type === 'failed') {
          liveStats.value.status = 'failed';
          store.dispatch('facebookImports/fetch', props.inboxId);
        } else if (data.type === 'paused') {
          liveStats.value.status = 'paused';
        }
      },
    }
  );
}

async function onPause() {
  await store.dispatch('facebookImports/pause', props.importJob.id);
  liveStats.value.status = 'paused';
  showAlert(t('FACEBOOK_IMPORT.ALERTS.IMPORT_PAUSED'));
}

async function onResume() {
  await store.dispatch('facebookImports/resume', props.importJob.id);
  liveStats.value.status = 'running';
  showAlert(t('FACEBOOK_IMPORT.ALERTS.IMPORT_RESUMED'));
}

async function onCancel() {
  await store.dispatch('facebookImports/cancel', props.importJob.id);
  liveStats.value.status = 'cancelled';
  showAlert(t('FACEBOOK_IMPORT.ALERTS.IMPORT_CANCELLED'));
}

onMounted(connectCable);
onUnmounted(() => subscription?.unsubscribe());
</script>

<template>
  <div class="mb-4 rounded-lg border border-n-weak p-4">
    <div class="mb-3 flex items-center justify-between">
      <h3 class="text-sm font-semibold text-n-slate-12">
        {{ t('FACEBOOK_IMPORT.PROGRESS.TITLE') }}
      </h3>
      <span
        class="rounded-full px-2 py-0.5 text-xs"
        :class="{
          'bg-woot-100 text-woot-700': isRunning,
          'bg-yellow-100 text-yellow-700': isPaused,
          'bg-green-100 text-green-700': liveStats.status === 'completed',
          'bg-red-100 text-red-700':
            liveStats.status === 'failed' || liveStats.status === 'cancelled',
        }"
      >
        {{ t(`FACEBOOK_IMPORT.STATUS.${liveStats.status}`) }}
      </span>
    </div>

    <div class="mb-3 h-2 w-full rounded-full bg-n-slate-3">
      <div
        class="h-2 rounded-full bg-woot-500 transition-all duration-500"
        :style="{ width: `${progressPercent}%` }"
      />
    </div>
    <p class="mb-3 text-xs text-n-slate-11">
      {{ liveStats.conversations_imported }} /
      {{ liveStats.total_conversations_found || '?' }}
      {{ t('FACEBOOK_IMPORT.PROGRESS.CONVERSATIONS') }}
      ({{ progressPercent }}%)
    </p>

    <div class="mb-4 grid grid-cols-4 gap-2 text-center">
      <div class="rounded bg-n-slate-2 p-2">
        <p class="text-base font-bold text-n-slate-12">
          {{ liveStats.conversations_imported }}
        </p>
        <p class="text-xs text-n-slate-11">
          {{ t('FACEBOOK_IMPORT.PROGRESS.CONVERSATIONS') }}
        </p>
      </div>
      <div class="rounded bg-n-slate-2 p-2">
        <p class="text-base font-bold text-n-slate-12">
          {{ liveStats.contacts_created }}
        </p>
        <p class="text-xs text-n-slate-11">
          {{ t('FACEBOOK_IMPORT.PROGRESS.CONTACTS') }}
        </p>
      </div>
      <div class="rounded bg-n-slate-2 p-2">
        <p class="text-base font-bold text-n-slate-12">
          {{ liveStats.messages_imported }}
        </p>
        <p class="text-xs text-n-slate-11">
          {{ t('FACEBOOK_IMPORT.PROGRESS.MESSAGES') }}
        </p>
      </div>
      <div class="rounded bg-n-slate-2 p-2">
        <p class="text-base font-bold text-n-slate-12">
          {{ liveStats.conversations_skipped }}
        </p>
        <p class="text-xs text-n-slate-11">
          {{ t('FACEBOOK_IMPORT.PROGRESS.SKIPPED') }}
        </p>
      </div>
    </div>

    <div v-if="liveLog.length > 0" class="mb-4">
      <p class="mb-1 text-xs font-medium text-n-slate-11">
        {{ t('FACEBOOK_IMPORT.PROGRESS.LIVE_LOG') }}
      </p>
      <div class="max-h-32 space-y-0.5 overflow-y-auto">
        <p
          v-for="(entry, i) in liveLog"
          :key="i"
          class="text-xs text-n-slate-11"
        >
          {{
            t('FACEBOOK_IMPORT.PROGRESS.LOG_ENTRY', {
              name: entry.contact_name,
              count: entry.message_count,
            })
          }}
        </p>
      </div>
    </div>

    <div v-if="isActive" class="flex gap-2">
      <button
        v-if="isRunning"
        class="rounded border border-n-weak px-3 py-1.5 text-xs text-n-slate-11 hover:bg-n-slate-3"
        @click="onPause"
      >
        {{ t('FACEBOOK_IMPORT.BUTTONS.PAUSE') }}
      </button>
      <button
        v-if="isPaused"
        class="rounded border border-woot-500 px-3 py-1.5 text-xs text-woot-600 hover:bg-woot-50"
        @click="onResume"
      >
        {{ t('FACEBOOK_IMPORT.BUTTONS.RESUME') }}
      </button>
      <button
        class="rounded border border-red-300 px-3 py-1.5 text-xs text-red-600 hover:bg-red-50"
        @click="onCancel"
      >
        {{ t('FACEBOOK_IMPORT.BUTTONS.CANCEL') }}
      </button>
    </div>
  </div>
</template>
